/**
 * Buttons.
 *
 * One primary per view. `secondary` is the default because most buttons on
 * an operator screen are not the main thing; `ghost` is for toolbars and row
 * actions; `danger` is reserved for the confirm button of something
 * destructive — the button that *opens* that confirm is `secondary` or
 * `danger-ghost`, so a red button always means "this is the last step".
 */
import type { Icon } from '@phosphor-icons/react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import { cx, ui } from './text';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

const ICON_SIZE: Record<ButtonSize, number> = { sm: 16, md: 18, lg: 20 };

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon (the inline-start side — right in Arabic). */
  icon?: Icon;
  /** Trailing icon. Directional glyphs should be mirrored by the caller's choice (CaretLeft means "forward" in RTL). */
  iconEnd?: Icon;
  /** Stretches to the container's inline size. */
  block?: boolean;
}

export interface ButtonProps extends CommonProps, ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Busy: the label stays (so the width does not jump and a screen reader
   * still knows what is happening), a spinner replaces the icon, and the
   * button refuses further clicks via `aria-disabled` rather than `disabled`
   * — a disabled button drops focus, which strands a keyboard user.
   */
  loading?: boolean;
}

/** A small indeterminate ring. Decorative: whatever is busy says so in words. */
export function Spinner({ size = 16 }: { size?: number }) {
  return <span className="spinner" style={{ inlineSize: size, blockSize: size }} aria-hidden="true" />;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon: Glyph, iconEnd: GlyphEnd, block = false, loading = false, className, children, onClick, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cx('btn', block && 'btn-block', className)}
      data-variant={variant}
      data-size={size}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={loading ? (event) => event.preventDefault() : onClick}
      {...rest}
    >
      {loading ? <Spinner size={ICON_SIZE[size]} /> : Glyph === undefined ? null : <Glyph size={ICON_SIZE[size]} aria-hidden="true" />}
      {children === undefined ? null : <span className="btn-label">{children}</span>}
      {GlyphEnd === undefined ? null : <GlyphEnd size={ICON_SIZE[size]} aria-hidden="true" />}
    </button>
  );
});

export interface ButtonLinkProps extends CommonProps, Omit<LinkProps, 'className'> {
  className?: string;
  children: ReactNode;
}

/** A navigation that looks like a button. It is still a link: middle-click and "open in new tab" work. */
export function ButtonLink({ variant = 'secondary', size = 'md', icon: Glyph, iconEnd: GlyphEnd, block = false, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={cx('btn', block && 'btn-block', className)} data-variant={variant} data-size={size} {...rest}>
      {Glyph === undefined ? null : <Glyph size={ICON_SIZE[size]} aria-hidden="true" />}
      <span className="btn-label">{children}</span>
      {GlyphEnd === undefined ? null : <GlyphEnd size={ICON_SIZE[size]} aria-hidden="true" />}
    </Link>
  );
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Required: an icon-only button has no other name. Also shown as the native tooltip. */
  label: string;
  icon: Icon;
  variant?: Exclude<ButtonVariant, 'danger'>;
  size?: ButtonSize;
  loading?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon: Glyph, variant = 'ghost', size = 'md', loading = false, className, type, title, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cx('btn', 'btn-icon', className)}
      data-variant={variant}
      data-size={size}
      aria-label={label}
      title={title ?? label}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={ICON_SIZE[size]} /> : <Glyph size={ICON_SIZE[size]} aria-hidden="true" />}
    </button>
  );
});

export interface IconButtonLinkProps extends Omit<LinkProps, 'className' | 'children' | 'aria-label'> {
  /** Required: the link has no other name. Say where it goes — «فتح نماذج مكتب الراشد», not «فتح». Also the native tooltip. */
  label: string;
  icon: Icon;
  variant?: 'secondary' | 'ghost';
  size?: ButtonSize;
  className?: string;
}

/**
 * A navigation that looks like an icon button — the caret at the end of a
 * spend row. Still a link (middle-click, open in a new tab), so it is never
 * a `button` with `navigate()` in its click handler.
 */
export function IconButtonLink({ label, icon: Glyph, variant = 'ghost', size = 'sm', className, title, ...rest }: IconButtonLinkProps) {
  return (
    <Link className={cx('btn', 'btn-icon', className)} data-variant={variant} data-size={size} aria-label={label} title={title ?? label} {...rest}>
      <Glyph size={ICON_SIZE[size]} aria-hidden="true" />
    </Link>
  );
}

/** For a status line beside a busy region: the spinner plus the word. */
export function Loading({ label }: { label?: string }) {
  return (
    <span className="loading-inline" role="status">
      <Spinner />
      <span>{label ?? ui('loading')}</span>
    </span>
  );
}
